import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, message, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DownOutlined, ReloadOutlined, UpOutlined } from '@ant-design/icons';

import AuthService from '../../../api/auth';
import ViewWrapper from '../../elements/ViewWrapper';
import { useAuth } from '../../../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchPermissions,
  getAllPermissions,
  getFetchingPermissions,
} from '../../../store/features/acl/permissions/permissionsSlice';
import {
  fetchRolesWithParams,
} from '../../../store/features/acl/roles/rolesSlice';
import { PermissionModel } from '../../../models/permission';
import { RoleModel } from '../../../models/role';

import './MyPermissionsView.less';

const { Text } = Typography;

const ROLES_FETCH_LIMIT = 500;

const MyPermissionsView = () => {
  const dispatch = useAppDispatch();
  const allPermissions = useAppSelector(getAllPermissions);
  const fetchingPermissions = useAppSelector(getFetchingPermissions);
  const rolesFromStore = useAppSelector((state) => state.roles.value);
  const { userPermissions, userRoles } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const userPermissionNames = useMemo(() => {
    const raw = userPermissions ?? [];
    return raw.map((p: string | { name?: string }) =>
      typeof p === 'string' ? p : (p?.name ?? '')
    ).filter(Boolean) as string[];
  }, [userPermissions]);

  const sortedPermissions = useMemo(() => {
    if (!allPermissions.length) return [];
    return [...allPermissions].sort((a, b) => {
      const aHas = userPermissionNames.includes(a.name);
      const bHas = userPermissionNames.includes(b.name);
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return (a.display_name || a.name).localeCompare(b.display_name || b.name);
    });
  }, [allPermissions, userPermissionNames]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (sortedPermissions.length > 0) {
      setExpandedRowKeys(sortedPermissions.map((r) => r.id));
    }
  }, [sortedPermissions]);

  useEffect(() => {
    dispatch(
      fetchRolesWithParams({
        params: {
          _limit: ROLES_FETCH_LIMIT,
          _offset: 0,
          _columns: 'permissions.*',
        },
      })
    );
  }, [dispatch]);

  const permissionToRolesMap = useMemo(() => {
    const map = new Map<string, RoleModel[]>();
    const userRoleIds = new Set(
      (userRoles ?? []).map((r: { id: string | number }) => String(r.id))
    );
    const userRolesWithPerms = (rolesFromStore ?? []).filter((r) =>
      userRoleIds.has(String(r.id))
    );
    for (const role of userRolesWithPerms) {
      const perms = role.permissions ?? [];
      for (const perm of perms) {
        const name = typeof perm === 'string' ? perm : perm?.name ?? '';
        if (!name) continue;
        const list = map.get(name) ?? [];
        if (!list.some((r) => String(r.id) === String(role.id))) list.push(role);
        map.set(name, list);
      }
    }
    return map;
  }, [userRoles, rolesFromStore]);

  const allExpanded =
    sortedPermissions.length > 0 &&
    expandedRowKeys.length === sortedPermissions.length;

  const handleToggleExpandAll = useCallback(() => {
    setExpandedRowKeys(
      allExpanded ? [] : sortedPermissions.map((r) => r.id)
    );
  }, [allExpanded, sortedPermissions]);

  const handleRefreshPermissions = useCallback(async () => {
    setRefreshing(true);
    try {
      await AuthService().refreshUserPermissions();
      message.success('Permissions refreshed. Changes are now in effect.');
    } catch (err) {
      message.error('Failed to refresh permissions. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const columns: ColumnsType<PermissionModel> = useMemo(
    () => [
      {
        title: 'Permission',
        dataIndex: 'display_name',
        key: 'display_name',
        width: '40%',
        render: (displayName: string, record: PermissionModel) => {
          const hasPermission = userPermissionNames.includes(record.name);
          return (
            <Text strong={hasPermission} type={hasPermission ? undefined : 'secondary'}>
              {displayName || record.name}
            </Text>
          );
        },
      },
      {
        title: 'Key',
        dataIndex: 'name',
        key: 'name',
        width: '30%',
        render: (name: string, record: PermissionModel) => {
          const hasPermission = userPermissionNames.includes(record.name);
          return (
            <Text type={hasPermission ? undefined : 'secondary'} className="permission-key">
              {name}
            </Text>
          );
        },
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description: string, record: PermissionModel) => {
          const hasPermission = userPermissionNames.includes(record.name);
          return (
            <Text type={hasPermission ? undefined : 'secondary'}>
              {description || '—'}
            </Text>
          );
        },
      },
    ],
    [userPermissionNames]
  );

  const expandable = useMemo(
    () => ({
      expandedRowKeys,
      onExpand: (expanded: boolean, record: PermissionModel) => {
        setExpandedRowKeys((prev) =>
          expanded
            ? [...prev, record.id]
            : prev.filter((key) => key !== record.id)
        );
      },
      expandedRowRender: (record: PermissionModel) => {
        const roles = permissionToRolesMap.get(record.name) ?? [];
        const label =
          roles.length > 0
            ? roles.map((r) => r.display_name || r.name).join(', ')
            : '—';
        return (
          <div className="my-permissions-inherited-row">
            <Text type="secondary">
              <strong>Inherited from:</strong> {label}
            </Text>
          </div>
        );
      },
    }),
    [permissionToRolesMap, expandedRowKeys]
  );

  return (
    <ViewWrapper
      headerTitle="My Permissions"
      headerSectionRight={
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={handleRefreshPermissions}
        >
          Refresh Permissions
        </Button>
      }
    >
      <Space direction="vertical" size="middle" className="my-permissions-content">
        <Text type="secondary">
          Permissions you have are shown at the top. Permissions you do not have are listed below and greyed out.
          Use &quot;Refresh Permissions&quot; to reload your permissions from the server without logging out.
        </Text>
        <Space style={{ marginBottom: 8 }}>
          <Button
            icon={allExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={handleToggleExpandAll}
            disabled={sortedPermissions.length === 0}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Space>
        <Table
          rowClassName={(record) =>
            userPermissionNames.includes(record.name) ? 'row-has-permission' : 'row-no-permission'
          }
          loading={fetchingPermissions && sortedPermissions.length === 0}
          rowKey="id"
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Total ${t} permissions` }}
          columns={columns}
          dataSource={sortedPermissions}
          expandable={expandable}
        />
      </Space>
    </ViewWrapper>
  );
};

export default MyPermissionsView;
